import { fetchWithWakeRetry } from '../../../sites/shared/browserApi';

const adminReviewSessionStorageKey = 'warren-platform-admin-review-token';

export type AdminReviewSiteKey = 'warren' | 'turtles';

export type AdminReviewPageSummary = {
  id: string;
  title: string;
  slug: string;
  status: string;
  templateKey: string;
  summary: string | null;
  legacySourceId: string | null;
  legacySourceUrl: string | null;
  createdAt: string;
  updatedAt: string;
  robotsIndex: boolean;
};

export type AdminReviewPagesResponse = {
  site: AdminReviewSite;
  count: number;
  pages: AdminReviewPageSummary[];
};

export type AdminReviewPageDetail = {
  site: AdminReviewSite;
  page: {
    id: string;
    title: string;
    slug: string;
    summary: string | null;
    bodyJson: string;
    templateKey: string;
    status: string;
    publishAt: string | null;
    unpublishAt: string | null;
    featuredMediaId: string | null;
    legacySourceId: string | null;
    legacySourceUrl: string | null;
    createdAt: string;
    updatedAt: string;
    seo: {
      title: string | null;
      description: string | null;
      canonicalUrl: string | null;
      robotsIndex: boolean;
      robotsFollow: boolean;
      openGraphTitle: string | null;
      openGraphDescription: string | null;
      openGraphMediaId: string | null;
      structuredDataJson: string | null;
    };
  };
};

export type AdminReviewSite = {
  key: string;
  name: string;
  primaryDomain: string;
};

export type AdminReviewDisclosureMedia = {
  id: string;
  storageProvider: string;
  storageKey: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  sha256: string;
  visibility: string;
};

export type AdminReviewDisclosureDocument = {
  id: string;
  title: string;
  documentType: string;
  status: string;
  isPublic: boolean;
  legacySourceId: string | null;
  legacySourceUrl: string | null;
  media: AdminReviewDisclosureMedia | null;
};

export type AdminReviewDisclosureItem = {
  id: string;
  key: string;
  label: string;
  valueText: string | null;
  externalUrl: string | null;
  fieldsJson: string;
  sortOrder: number;
  validFrom: string | null;
  validUntil: string | null;
  assetState: 'not-linked' | 'ready' | 'drift' | 'missing-document' | 'missing-media';
  document: AdminReviewDisclosureDocument | null;
};

export type AdminReviewDisclosureSection = {
  id: string;
  key: string;
  title: string;
  status: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
  items: AdminReviewDisclosureItem[];
};

export type AdminReviewMandatoryDisclosureResponse = {
  site: AdminReviewSite;
  sectionCount: number;
  itemCount: number;
  linkedDocumentCount: number;
  sections: AdminReviewDisclosureSection[];
};

export type AdminReviewErrorCode =
  | 'not-configured'
  | 'not-enabled'
  | 'unauthorized'
  | 'not-found'
  | 'unavailable'
  | 'unexpected';

export class AdminReviewApiError extends Error {
  constructor(
    public readonly code: AdminReviewErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'AdminReviewApiError';
  }
}

function getApiBaseUrl() {
  return (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');
}

export function getStoredAdminReviewToken() {
  return window.sessionStorage.getItem(adminReviewSessionStorageKey) ?? '';
}

export function clearStoredAdminReviewToken() {
  window.sessionStorage.removeItem(adminReviewSessionStorageKey);
}

function storeAdminReviewToken(token: string) {
  window.sessionStorage.setItem(adminReviewSessionStorageKey, token);
}

async function readErrorName(response: Response) {
  try {
    const payload = await response.json() as { error?: unknown };
    return typeof payload.error === 'string' ? payload.error : null;
  } catch {
    return null;
  }
}

async function fetchAuthorized(
  path: string,
  token: string,
  signal?: AbortSignal,
) {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new AdminReviewApiError(
      'not-configured',
      'The Admin API origin is not configured for this environment.',
    );
  }

  const response = await fetchWithWakeRetry(
    `${apiBaseUrl}${path}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-store',
    },
    {
      signal,
      wakeAfterMs: 7_000,
      maxWaitMs: 75_000,
      retryDelayMs: 4_000,
      requestTimeoutMs: 12_000,
      retryStatusCodes: [408, 425, 429, 500, 502, 503, 504],
    },
  );

  if (!response) {
    throw new AdminReviewApiError(
      'unavailable',
      'The staging Admin review service did not become available in time.',
    );
  }

  if (response.status === 401) {
    throw new AdminReviewApiError(
      'unauthorized',
      'The staging review access key was not accepted.',
    );
  }

  if (response.status === 404) {
    const errorName = await readErrorName(response);
    if (errorName === 'site_not_found' || errorName === 'page_not_found') {
      throw new AdminReviewApiError(
        'not-found',
        errorName === 'site_not_found'
          ? 'This site has not been created in the staging database.'
          : 'This Page no longer exists in the selected site.',
      );
    }

    throw new AdminReviewApiError(
      'not-enabled',
      'The protected Admin review endpoint is not enabled on the staging API.',
    );
  }

  if (!response.ok) {
    throw new AdminReviewApiError(
      'unexpected',
      `The staging Admin review request failed with HTTP ${response.status}.`,
    );
  }

  return response;
}

export async function connectAdminReviewSession(
  accessKey: string,
  signal?: AbortSignal,
) {
  const normalizedAccessKey = accessKey.trim();
  if (!normalizedAccessKey) {
    throw new AdminReviewApiError(
      'unauthorized',
      'Enter the staging review access key.',
    );
  }

  await fetchAuthorized('/api/v1/admin-review/session', normalizedAccessKey, signal);
  storeAdminReviewToken(normalizedAccessKey);
  return normalizedAccessKey;
}

export async function getAdminReviewPages(
  siteKey: AdminReviewSiteKey,
  token: string,
  signal?: AbortSignal,
) {
  const response = await fetchAuthorized(
    `/api/v1/admin-review/sites/${encodeURIComponent(siteKey)}/pages`,
    token,
    signal,
  );

  return await response.json() as AdminReviewPagesResponse;
}

export async function getAdminReviewPage(
  siteKey: AdminReviewSiteKey,
  pageId: string,
  token: string,
  signal?: AbortSignal,
) {
  const response = await fetchAuthorized(
    `/api/v1/admin-review/sites/${encodeURIComponent(siteKey)}/pages/${encodeURIComponent(pageId)}`,
    token,
    signal,
  );

  return await response.json() as AdminReviewPageDetail;
}

export async function getAdminReviewMandatoryDisclosure(
  siteKey: AdminReviewSiteKey,
  token: string,
  signal?: AbortSignal,
) {
  const response = await fetchAuthorized(
    `/api/v1/admin-review/sites/${encodeURIComponent(siteKey)}/mandatory-disclosure`,
    token,
    signal,
  );

  return await response.json() as AdminReviewMandatoryDisclosureResponse;
}
